/**
 * TenantProvider (TL-050, TL-052, TL-053, TL-054, TL-057).
 *
 * Calls `GET /tenant/` once, then brands the page: colour tokens, document
 * title and favicon. It also listens for the API client's signals (402
 * `workspace_paused`, 404 `tenant_not_found`) and moves the app into the
 * matching phase. `App` reads the phase and shows the right screen.
 *
 * While loading it shows a neutral ring with no logo, so no institute's
 * brand flashes for another's.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { ApiError } from "@/lib/api/errors";
import { tenantApi, FEATURE_NAMES, type FeatureName, type Tenant } from "@/lib/api/tenant";
import "@/components/brand/brandReveal.css";

import { missingSlug, reconcileBasePath } from "./basePath";
import { applyTenantBrand } from "./applyBrand";
import { applyDocumentIdentity } from "./documentIdentity";
import { onTenantSignal } from "./signals";
import { TenantContext, type TenantContextValue, type TenantPhase } from "./useTenant";

const QUERY_KEY = ["tenant"] as const;

const FALLBACK: Tenant = {
  schema: "",
  slug: "",
  name: "TutorLoop",
  displayName: "TutorLoop",
  status: "active",
  isInternal: false,
  logoUrl: null,
  logoMarkUrl: null,
  basePath: "",
  primaryColour: null,
  accentColour: null,
  supportEmail: null,
  features: Object.fromEntries(FEATURE_NAMES.map((n) => [n, false])) as Record<FeatureName, boolean>,
  loginOptions: { magicLink: true, usernameLogin: true, signup: false },
  plan: null,
  setupComplete: true,
  emailSenderName: null,
  replyToEmail: null,
};

function TenantLoader() {
  return (
    <div className="brl brl--route" role="status" aria-live="polite" aria-label="Loading">
      <div className="brl__orbit">
        <span className="brl__ring" aria-hidden />
      </div>
    </div>
  );
}

function TenantLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="alert"
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: "var(--space-6)",
        background: "var(--color-page)",
        color: "var(--color-primary)",
        fontFamily: "var(--font-sans)",
        textAlign: "center",
      }}
    >
      <div style={{ maxWidth: 360 }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: "var(--text-xl)", margin: 0 }}>
          We can't reach this workspace
        </h1>
        <p style={{ color: "var(--color-muted)", margin: "var(--space-3) 0 var(--space-5)" }}>
          Check your connection and try again.
        </p>
        <button
          type="button"
          onClick={onRetry}
          style={{
            minHeight: 44,
            padding: "0 var(--space-6)",
            borderRadius: "var(--radius-pill)",
            border: 0,
            cursor: "pointer",
            background: "var(--brand-gradient)",
            color: "var(--color-on-accent)",
            font: "inherit",
            fontWeight: 600,
          }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [signal, setSignal] = useState<"paused" | "not_found" | null>(null);

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: ({ signal: abort }) => tenantApi.get(abort),
    enabled: !missingSlug,
    staleTime: Infinity,
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 2,
  });

  useEffect(() => onTenantSignal(setSignal), []);

  const tenant = query.data ?? null;

  useEffect(() => {
    if (!tenant) return;
    if (reconcileBasePath(tenant.basePath)) return;
    applyTenantBrand({ primary: tenant.primaryColour, accent: tenant.accentColour });
    applyDocumentIdentity(tenant);
  }, [tenant]);

  const refresh = useCallback(async () => {
    setSignal(null);
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }, [queryClient]);

  const notFound =
    missingSlug ||
    signal === "not_found" || (query.error instanceof ApiError && query.error.code === "tenant_not_found");

  let phase: TenantPhase;
  if (notFound) phase = "not_found";
  else if (query.isPending) phase = "loading";
  else if (!tenant) phase = "error";
  else if (signal === "paused" || tenant.status === "suspended" || tenant.status === "cancelled") phase = "paused";
  else phase = "ready";

  const value = useMemo<TenantContextValue>(
    () => ({ tenant: tenant ?? FALLBACK, phase, refresh }),
    [tenant, phase, refresh],
  );

  if (phase === "loading") return <TenantLoader />;
  if (phase === "error") return <TenantLoadError onRetry={() => void query.refetch()} />;

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}
